import React from "react";
import { motion } from "framer-motion";
import { TIMELINE } from "../data/content";
import { Briefcase, ClipboardList, BadgeCheck, Gauge, FileText, Users } from "lucide-react";

const iconMap = {
  briefcase: Briefcase,
  "clipboard-list": ClipboardList,
  "badge-check": BadgeCheck,
  gauge: Gauge,
  "file-text": FileText,
  users: Users,
};

export default function AssessmentTimeline() {
  return (
    <section className="py-20 sm:py-28 bg-gradient-to-b from-muted/50 via-background to-muted/40 dark:from-muted/20 dark:to-muted/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-secondary">The Process</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            From assessment to <span className="text-gradient-brand">Cyber Score</span> — in six steps
          </h2>
          <p className="mt-4 text-muted-foreground text-base sm:text-lg leading-relaxed">
            A structured, senior-consultant-led journey that turns your business context into an executive-ready
            cybersecurity readout — with zero disruption to your team.
          </p>
        </div>

        {/* Desktop horizontal timeline */}
        <div className="mt-14 hidden lg:block relative">
          <div className="absolute top-8 left-8 right-8 h-1 rounded-full bg-gradient-to-r from-primary via-accent to-secondary opacity-40" />
          <div className="grid grid-cols-6 gap-4 relative">
            {TIMELINE.map((t, i) => {
              const Icon = iconMap[t.icon] || Briefcase;
              return (
                <motion.div
                  key={t.step}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  data-testid={`timeline-step-${t.step}`}
                  className="text-center"
                >
                  <div className="mx-auto h-16 w-16 rounded-2xl bg-gradient-to-br from-primary to-accent text-white grid place-items-center shadow-xl shadow-primary/20 dot-glow relative z-10">
                    <Icon className="h-6 w-6" strokeWidth={1.6} />
                    <div className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-secondary text-white text-[11px] font-black grid place-items-center border-2 border-background">
                      {t.step}
                    </div>
                  </div>
                  <div className="mt-4 rounded-2xl border border-border bg-card p-4 card-lift">
                    <div className="font-display text-base font-bold">{t.title}</div>
                    <div className="mt-1 text-[13px] text-muted-foreground leading-relaxed">{t.desc}</div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Mobile vertical timeline */}
        <div className="mt-12 lg:hidden relative">
          <div className="absolute left-8 top-2 bottom-2 w-1 rounded-full bg-gradient-to-b from-primary via-accent to-secondary opacity-40" />
          {TIMELINE.map((t, i) => {
            const Icon = iconMap[t.icon] || Briefcase;
            return (
              <motion.div
                key={t.step}
                initial={{ opacity: 0, x: -12 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="relative pl-24 pb-8 last:pb-0"
                data-testid={`timeline-step-${t.step}`}
              >
                <div className="absolute left-0 top-0 h-16 w-16 rounded-2xl bg-gradient-to-br from-primary to-accent text-white grid place-items-center shadow-lg z-10">
                  <Icon className="h-6 w-6" strokeWidth={1.6} />
                  <div className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-secondary text-white text-[10px] font-black grid place-items-center border-2 border-background">
                    {t.step}
                  </div>
                </div>
                <div className="rounded-2xl border border-border bg-card p-4 sm:p-5">
                  <div className="font-display text-lg font-semibold">{t.title}</div>
                  <div className="mt-1 text-sm text-muted-foreground leading-relaxed">{t.desc}</div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
